
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft, Download, Loader2, Zap, FileDown,
  Layout, Calendar, MapPin, X, Mail, Phone, KeyRound, Building2, Banknote
} from 'lucide-react';
import DocumentsOrganisation from '../components/DocumentsOrganisation';
import { motion, AnimatePresence } from 'framer-motion';
import { adminApi } from '../services/api.jsx';
import { apercuRapport, telechargerRapport } from '../utils/rapport';
import { useDroits } from '../context/Droits';
import logo from '../assets/img/logo.png';
import { restitutionDuScore, TableauResultats } from '../components/resultats/Resultats';
import {
  annoncerErreur, annoncerSucces, BarreProgression, EtatErreur, EtatSansDonnees,
  Squelette, SqueletteCarte, SqueletteGraphique, telecharger,
} from '../etats';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OrgDetails() {
  const { peut } = useDroits();
  const peutExporter = peut('diagnostics', 'exporter');
  const { id } = useParams();
  const navigate = useNavigate();
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showViewer, setShowViewer] = useState(false);
  const [invoiceDownloading, setInvoiceDownloading] = useState(false);
  // Le rapport vient du serveur : l'aperçu affiche le PDF lui-même, si bien
  // qu'il ne peut pas différer du fichier téléchargé.
  const [rapportEnCours, setRapportEnCours] = useState(false);
  const [rapportErreur, setRapportErreur] = useState('');
  const [apercuUrl, setApercuUrl] = useState(null);
  const scoreTotal = result?.score_total;

  const enregistrerLeRapport = async () => {
    if (!result?.id) return;
    setRapportEnCours(true);
    setRapportErreur('');
    try {
      await telechargerRapport(result.id);
      annoncerSucces('Rapport téléchargé.');
    } catch (souci) {
      setRapportErreur(souci.message);
    } finally {
      setRapportEnCours(false);
    }
  };

  // L'aperçu charge le PDF servi par la route, puis libère son URL d'objet à
  // la fermeture : sans cela, chaque ouverture laisserait un document entier
  // en mémoire pour toute la durée de la session.
  useEffect(() => {
    if (!showViewer || !result?.id) return undefined;
    let vivant = true;
    let url = null;
    setRapportErreur('');
    apercuRapport(result.id)
      .then((adresse) => {
        if (!vivant) { window.URL.revokeObjectURL(adresse); return; }
        url = adresse;
        setApercuUrl(adresse);
      })
      .catch((souci) => vivant && setRapportErreur(souci.message));
    return () => {
      vivant = false;
      setApercuUrl(null);
      if (url) window.URL.revokeObjectURL(url);
    };
  }, [showViewer, result?.id]);

  // Relançable depuis l'écran d'erreur (« Réessayer »).
  const [essai, setEssai] = useState(0);
  useEffect(() => {
    const loadResult = async () => {
      setLoading(true);
      setError(null);
      try {
        const data = await adminApi.getDiagnosticByOrg(id);
        setResult(data);
      } catch (e) {
        // L'objet entier, pas son message : son statut dit lequel des cas.
        setError(e);
      } finally {
        setLoading(false);
      }
    };
    if (id) loadResult();
  }, [id, essai]);


  const domainEntries = useMemo(() => result?.domain_scores ? Object.entries(result.domain_scores) : [], [result]);


  // Premier chargement seulement : une relance garde le résultat affiché.
  if (loading && !result) return (
    <div className="space-y-6" role="status" aria-label="Chargement du diagnostic">
      <div>
        <Squelette className="h-4 w-40 mb-4" />
        <Squelette className="h-8 w-80 max-w-full mb-3" />
        <Squelette className="h-3 w-56" />
      </div>
      <div className="grid md:grid-cols-3 gap-4">
        {[0, 1, 2].map((i) => <SqueletteCarte key={i} />)}
      </div>
      <div className="bg-[var(--bg-card)] rounded-carte shadow-carte p-6">
        <SqueletteGraphique className="h-56" />
      </div>
    </div>
  );

  if (error || !result) return (
    <div className="flex flex-col items-center justify-center min-h-[70vh] text-center p-10">
      {/* Un 404 ici veut dire « pas encore de diagnostic », pas une page
          disparue : on le dit dans ces termes. */}
      {!error || error.statut === 404 ? (
        <EtatSansDonnees cas="vide" icone={Zap} titre="Aucun diagnostic pour cette organisation"
          aide="Elle n’a pas encore répondu au questionnaire." />
      ) : (
        <EtatErreur erreur={error} onReessayer={() => setEssai((n) => n + 1)} />
      )}
      <button
        type="button"
        onClick={() => navigate('/users', { replace: false })}
        className="act act-contour mt-8"
      >
        <ArrowLeft className="w-4 h-4" /> Retour à la liste des organisations
      </button>
      {/* Sans diagnostic, l'organisation peut tout de même avoir déposé
          des pièces : elles ne doivent pas devenir inatteignables. */}
      {id && <div className="w-full max-w-4xl mt-10 text-left"><DocumentsOrganisation organisationId={id} /></div>}
    </div>
  );

  const handleBack = () => {
    navigate('/users', { replace: false });
  };

  /* Le budget est saisi EN DOLLARS dans le formulaire d'identification ;
     la base porte l'équivalent en francs (environ 600 pour un). Le serveur
     compose désormais la valeur AVEC son unité, et c'est elle qu'on pose.

     Recomposer ici était le défaut : quand l'équivalent manquait, on
     retombait sur le montant en dollars sous une étiquette « FCFA » — un
     budget annoncé six cents fois trop petit, sans que rien ne le trahisse
     puisque le nombre reste plausible dans les deux monnaies. */
  const budgetAnnuel = () =>
    result.organisation?.budget_annuel_display || 'Non renseigné';

  return (
    <div className="min-h-full space-y-6 animate-in pb-8 flex flex-col">
      {/* ── L'en-tête ─────────────────────────────────────────────────
          Le nom de l'organisation EST le titre : c'est d'elle qu'on
          parle. « Analyse du diagnostic » passe en surtitre — la même
          phrase sur toutes les fiches ne distingue rien, et à 36 px
          elle occupait la place du seul mot qui compte ici.

          Le filet inférieur disparaît : dans le modèle, c'est le vide
          entre les blocs qui sépare. */}
      <header className="flex flex-wrap items-end gap-4 shrink-0">
        <div className="flex-[1_1_320px] min-w-0">
          <button
            type="button"
            onClick={handleBack}
            className="act act-sm act-contour mb-3"
          >
            <ArrowLeft className="w-4 h-4 shrink-0" /> Retour aux organisations
          </button>
          <p className="surtitre text-[var(--text-muted)]">Analyse du diagnostic</p>
          <h1 className="titre-page mt-1">
            {result.organisation?.nom || 'Organisation non renseignée'}
          </h1>
          <div className="flex flex-wrap items-center gap-2 mt-2.5">
            <span className="etiquette etiq-cours">
              {result.organisation?.type || 'Type non renseigné'}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--text-muted)]">
              <MapPin className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
              {result.organisation?.ville || '—'}, {result.organisation?.pays || '—'}
            </span>
            <span className="inline-flex items-center gap-1.5 text-[12.5px] text-[var(--text-muted)]">
              <Calendar className="w-3.5 h-3.5 shrink-0" strokeWidth={1.8} />
              Audit du {result.date ? new Date(result.date).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : '—'}
            </span>
          </div>
        </div>

        <div className="flex gap-4 w-full lg:w-auto shrink-0 flex-wrap">
          {/* Aperçu et téléchargement sortent le même document du serveur :
              les deux relèvent donc du droit « exporter », et disparaissent
              ensemble pour un rôle à qui il est fermé. Les chiffres du
              diagnostic restent lisibles à l'écran. */}
          {peutExporter && (
            <>
              <button
                type="button"
                onClick={() => setShowViewer(true)}
                className="act act-contour flex-1"
              >
                <Layout className="w-4 h-4" /> Aperçu Rapport
              </button>
              <button
                type="button"
                onClick={enregistrerLeRapport}
                disabled={rapportEnCours} aria-busy={rapportEnCours || undefined}
                className="act flex-1 lg:flex-none"
                title="La mise en page du rapport prend une quinzaine de secondes"
              >
                {rapportEnCours ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                {rapportEnCours ? 'Génération du PDF…' : 'Télécharger le PDF'}
              </button>
            </>
          )}
          {result.payment?.status === 'completed' && result.id && (
            <button
              type="button"
              disabled={invoiceDownloading} aria-busy={invoiceDownloading || undefined}
              onClick={async () => {
                setInvoiceDownloading(true);
                try {
                  await telecharger(`${API_BASE}/payments/invoice/${result.id}/`,
                    `recu_${(result.organisation?.nom || 'paiement').replace(/\s+/g, '_')}.pdf`);
                  annoncerSucces('Reçu téléchargé.');
                } catch (err) {
                  annoncerErreur(`Impossible de télécharger le reçu : ${err.message}`);
                } finally {
                  setInvoiceDownloading(false);
                }
              }}
              className="act act-contour flex-1"
            >
              {invoiceDownloading ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4" />}
              {invoiceDownloading ? 'Génération...' : 'Reçu Paiement'}
            </button>
          )}
        </div>
        {rapportErreur && !showViewer && (
          <p role="alert" className="mt-4 text-[var(--alerte)] text-xs">{rapportErreur}</p>
        )}
      </header>

      <section className="bg-[var(--bg-card)] shadow-carte rounded-carte p-6 shrink-0">
        <h2 className="titre-carte mb-5">
          Fiche de l'organisation
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Nom</p>
            <p className="text-sm font-semibold text-white leading-snug flex items-start gap-2">
              <Building2 className="w-4 h-4 text-[var(--accent-texte)] mt-0.5 shrink-0" />
              {result.organisation?.nom || 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Type</p>
            <p className="text-sm font-semibold text-white leading-snug">
              {result.organisation?.type || 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Email</p>
            <p className="text-sm font-semibold text-white leading-snug flex items-start gap-2 break-all">
              <Mail className="w-4 h-4 text-[var(--accent-texte)] mt-0.5 shrink-0" />
              {result.organisation?.email || 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Contact</p>
            <p className="text-sm font-semibold text-white leading-snug flex items-start gap-2">
              <Phone className="w-4 h-4 text-[var(--accent-texte)] mt-0.5 shrink-0" />
              {result.organisation?.contact || 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Ville</p>
            <p className="text-sm font-semibold text-white leading-snug">
              {result.organisation?.ville || 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Pays</p>
            <p className="text-sm font-semibold text-white leading-snug">
              {result.organisation?.pays || 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Code acces</p>
            <p className="text-sm font-semibold text-white leading-snug flex items-start gap-2">
              <KeyRound className="w-4 h-4 text-[var(--accent-texte)] mt-0.5 shrink-0" />
              {result.organisation?.access_code || 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Date audit</p>
            <p className="text-sm font-semibold text-white leading-snug flex items-start gap-2">
              <Calendar className="w-4 h-4 text-[var(--accent-texte)] mt-0.5 shrink-0" />
              {result.date ? new Date(result.date).toLocaleDateString('fr-FR') : 'N/A'}
            </p>
          </div>
          <div className="rounded-pastille shadow-carte bg-[var(--bg-card)] p-4">
            <p className="text-[12.5px] text-[var(--text-muted)] mb-1">Budget annuel</p>
            <p className="text-sm font-semibold text-white leading-snug flex items-start gap-2">
              <Banknote className="w-4 h-4 text-[var(--accent-texte)] mt-0.5 shrink-0" />
              {budgetAnnuel()}
            </p>
          </div>
        </div>
      </section>

      {/* Les pièces déposées, juste sous la fiche : on n'a plus à passer
          par les projets pour les lire ni pour les valider. */}
      <DocumentsOrganisation organisationId={id} />

      {/* L'ordre de lecture commun à toutes les pages de résultats. */}
      <TableauResultats
        score={Number(scoreTotal) || 0}
        domaines={domainEntries.map(([nom, score]) => ({ nom, score: Number(score) || 0 }))}
        restitution={<p>{result.restitution || restitutionDuScore(result?.organisation?.nom, scoreTotal)}</p>}
        forces={(result.forces || []).map((f) => ({ titre: f.sous_domaine, description: f.description }))}
        faiblesses={(result.faiblesses || []).map((f) => ({ titre: f.sous_domaine, description: f.description }))}
        actions={(result.actions_correctives || []).map((a) => ({
          description: a.description || a.action, priorite: a.priorite, domaine: a.domaine,
          sous_domaine: a.sous_domaine, niveau: a.niveau, livrables: a.livrables,
        }))}
        introPlan="Recommandations pour l'optimisation organisationnelle — le contenu du rapport complet."
        videPlan="Aucune recommandation pour ce diagnostic."
      />

      <AnimatePresence>
        {showViewer && (
          <motion.div
            /* Pas d'animation de SORTIE : avec un <iframe> dans le sous-arbre,
               framer-motion ne termine pas la transition. Le calque restait
               alors en place à `opacity: 0`, invisible mais avec
               `pointer-events: auto` — il avalait tous les clics et la page
               devenait inutilisable après avoir fermé l'aperçu. */
            className="fixed inset-0 z-[100] bg-[var(--text-muted)]/95 backdrop-blur-3xl flex flex-col"
            initial={{ opacity: 0 }} animate={{ opacity: 1 }}
          >
            <div className="min-h-24 bg-[var(--bg-card)] border-b border-[var(--border)] flex flex-col lg:flex-row lg:items-center justify-between gap-4 px-4 sm:px-6 lg:px-10 py-4 relative z-[110]">
               <div className="flex items-center gap-4 lg:gap-6">
                  <img src={logo} alt="Asso-pro Logo" className="h-10 md:h-12 w-auto object-contain max-w-full shrink-0" />
                  <div>
                    <h4 className="text-white font-semibold text-xs uppercase tracking-widest">Rapport Stratégique Final</h4>
                    <p className="surtitre text-[var(--text-muted)] mt-1">Génération temps réel pour {result.organisation?.nom || 'Structure'}</p>
                  </div>
               </div>
               <div className="w-full lg:w-auto flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-4 lg:gap-6">
                  <button
                    type="button"
                    onClick={enregistrerLeRapport}
                    disabled={rapportEnCours} aria-busy={rapportEnCours || undefined}
                    className={`flex items-center justify-center gap-3 px-6 lg:px-10 py-3 lg:py-4 bg-[#5EBD4F] text-[#0A1128] font-semibold rounded-pastille hover:scale-105 transition-all text-[10px] ${rapportEnCours ? 'opacity-50 pointer-events-none' : ''}`}
                  >
                    {rapportEnCours ? 'Génération…' : 'Sauvegarder PDF'}
                  </button>
                  <button type="button" onClick={() => setShowViewer(false)} className="act act-danger">
                    <X className="w-4 h-4" /> Fermer
                  </button>
               </div>
            </div>
            {/* L'aperçu affiche le PDF lui-même, pas une reconstitution : il
                ne peut donc pas s'écarter du fichier téléchargé. */}
            <div className="flex-1 overflow-hidden bg-[var(--text-muted)]/40">
              {rapportErreur ? (
                <div className="h-full flex items-center justify-center px-6 text-center">
                  <p className="text-[var(--alerte)] text-sm">{rapportErreur}</p>
                </div>
              ) : apercuUrl ? (
                <iframe
                  src={apercuUrl}
                  title={`Rapport de diagnostic — ${result.organisation?.nom || 'organisation'}`}
                  className="w-full h-full border-0 bg-white"
                />
              ) : (
                <div className="h-full flex items-center justify-center px-6">
                  <div className="w-full max-w-sm rounded-pastille bg-[var(--bg-card)] p-5 shadow-carte">
                    <BarreProgression libelle="Génération du rapport…" />
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
